"use client";

import {
  genPlainDateList,
  getCurrentDate,
  PlainDate,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";
import { useRouter, useSearchParams } from "next/navigation";
import {
  createParser,
  parseAsString,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { STAY_TICKET_MASTER_CONFIG } from "../../../constants";
import { useGtm } from "../../../lib/google-tag-manager/gtm";
import { recordReservationFormEntryTimeIfNeeded } from "../../../lib/google-tag-manager/helpers/reservation-form";
import type { UnavailablePeriod } from "../dx/ui/date-picker";
import type { SelectedAccommodationReservation } from "../reservation-form/reservation-calendar/reservation-calendar";
import { RoomDetailDrawerProvider } from "../room-detail/room-detail-drawer-provider";
import { SiteDetailDrawerProvider } from "../site-detail/site-detail-drawer-provider";
import { SiteDetailDrawerUrlSync } from "../site-detail/site-detail-drawer-url-sync";

import { AvailabilityStripProvider } from "./_context/availability-strip-context";
import {
  RoomTypeReserveProvider,
  type RoomTypeReserveContextValue,
} from "./_context/room-type-reserve-context";
import { useArchitectureTypeFilter } from "./_hooks/use-architecture-type-filter";
import { useAreaFilter } from "./_hooks/use-area-filter";
import { useFacilityFilter } from "./_hooks/use-facility-filter";
import { useRoomTypeFilterTracking } from "./_hooks/use-room-type-filter-tracking";
import type {
  BrowseArea,
  BrowseRoomType,
  BrowseSite,
} from "./_hooks/use-room-type-search";
import { useRoomTypeSearch } from "./_hooks/use-room-type-search";
import {
  type SearchCalendarArea,
  type SearchCalendarRoomType,
  type SearchCalendarSite,
  useSearchCalendarAvailability,
} from "./_hooks/use-search-calendar-availability";
import { buildDirectConfirmUrl } from "./_utils/build-direct-confirm-url";
import { buildSearchCalendarDateInfoMap } from "./_utils/build-search-calendar-date-info-map";
import { getSearchCalendarRenderRange } from "./_utils/get-search-calendar-render-range";
import {
  countVisibleInventory,
  selectVisibleAreas,
} from "./_utils/select-visible-areas";
import { BrowseLayout } from "./browse/browse-layout";
import type { ApplyConditions } from "./browse/filters/condition-filter-popup";
import { PcSidebar } from "./browse/filters/pc-sidebar";
import { SpTopbar } from "./browse/filters/sp-topbar";
import { RoomTypeSearchEmptyState } from "./browse/results/room-type-search-empty-state";
import { SiteList } from "./browse/results/site-list";
import { SiteListSkeleton } from "./browse/results/site-list-skeleton";
import type { RoomTypeReservationConfirmOptions } from "./reserve/room-type-reservation-calendar";

const parseAsPlainDate = createParser({
  parse: (value) => {
    try {
      return PlainDate.from(value);
    } catch {
      return null;
    }
  },
  serialize: (value: PlainDate) => value.toString(),
});

// URL は legacy /reservation-form と同じ ci(チェックイン)/co(チェックアウト) 形式で持つ。
// アプリ内部は firstNight/lastNight（泊）で扱うため、この nuqs 境界で +1/-1 変換する。
const dateRangeParams = {
  ci: parseAsPlainDate,
  co: parseAsPlainDate,
};

/**
 * 部屋タイプ予約のルートコンテナ。日程確定時は確定ページへ遷移する。
 * 部屋詳細 drawer・拠点詳細モーダルは呼び出し側の関心事ではなくこのコンテナの実装詳細のため、
 * `RoomDetailDrawerProvider` / `SiteDetailDrawerProvider` をここで抱え、呼び出し側での wrap を不要にする。
 */
export function RoomTypeReservationContainer({
  stayTicketUnitId,
  stayTicketMasterId,
  minNight,
  maxStayNights,
  unavailablePeriods,
}: {
  stayTicketUnitId: string;
  stayTicketMasterId: string | null;
  minNight: PlainDate;
  maxStayNights: number;
  unavailablePeriods?: readonly UnavailablePeriod[];
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // 「一覧へ戻る」導線用の、検索条件込みの遷移元フルパス（エンコード前）。
  const currentListQuery = searchParams.toString();
  const roomTypeListPath =
    currentListQuery !== ""
      ? `/ticket-reservations/form?${currentListQuery}`
      : "/ticket-reservations/form";
  const gtm = useGtm();
  const listViewedSentRef = useRef(false);

  function handleDraftSelected(
    selection: SelectedAccommodationReservation & { roomTypeId: string },
    { stayTicketTemplateLookupKey }: RoomTypeReservationConfirmOptions,
  ) {
    if (stayTicketTemplateLookupKey !== null) {
      window.location.assign(
        buildDirectConfirmUrl({
          stayTicketTemplateLookupKey,
          roomTypeId: selection.roomTypeId,
          selection,
          evChargerAvailable,
          returnTo: roomTypeListPath,
        }),
      );
      return;
    }
    // /ticket-issue で発行し直すとチケットの LINE ID が引き継がれず LINE ユーザーとの紐付けが切れるため、
    // 既存ユニットのまま確認画面へ進める。
    const params = new URLSearchParams();
    params.set("stayTicketUnitId", stayTicketUnitId);
    params.set("accommodationId", selection.accommodationId);
    params.set("roomTypeId", selection.roomTypeId);
    params.set("ci", parseAsPlainDate.serialize(selection.firstNight));
    params.set(
      "co",
      parseAsPlainDate.serialize(selection.lastNight.add({ days: 1 })),
    );
    for (const facility of selection.accommodationFacilities) {
      params.append("accommodationFacility", facility);
    }
    // 確定ページは ev で EV 条件付きの空室から部屋を選び直すため、EV フィルタ状態を引き継ぐ。
    // 無効時はパラメータ自体を付けない（有効時のみ存在するURLにする既存のフィルタ系と同様の方針）。
    if (evChargerAvailable) {
      params.set("ev", "true");
    }
    params.set("returnTo", roomTypeListPath);
    router.push(`/ticket-reservations/form/confirm?${params.toString()}`);
  }

  const { areaFilter, toggleArea, applyAreaSlugs } = useAreaFilter();
  const clearAreaFilter = () => applyAreaSlugs([]);
  const {
    facilityFilter,
    toggleFacility,
    evChargerAvailable,
    toggleEvCharger,
    applyFacilityConditions,
  } = useFacilityFilter();
  const {
    architectureFilter,
    largeOnly,
    toggleArchitectureType,
    toggleLargeOnly,
    applyArchitectureConditions,
  } = useArchitectureTypeFilter();

  const [{ ci, co }, setDateRange] = useQueryStates(dateRangeParams);
  const [siteDetail, setSiteDetail] = useQueryState(
    "siteDetail",
    parseAsString,
  );
  const handleSiteDetailDrawerDidClose = useCallback(
    () => void setSiteDetail(null),
    [setSiteDetail],
  );
  // URL の ci/co（チェックイン/チェックアウト）→ 内部の firstNight/lastNight（泊）へ。
  // lastNight = co - 1（チェックアウト前夜が最終泊）。
  const firstNight = ci;
  // co.subtract は毎レンダー新しい PlainDate を返すため、下流の useMemo（reserveContextValue）の
  // 依存値として安定した参照になるようここでメモ化する
  // （React Compiler の react-hooks/preserve-manual-memoization がこれを検知して警告するため必須）。
  const lastNight = useMemo(
    () => (co !== null ? co.subtract({ days: 1 }) : null),
    [co],
  );

  // 予約完了時の time_to_reservation 計測の起点。非 roomType は ReservationFormBase 表示時に
  // 記録するが、roomType はそこを通らず確定ページへ遷移する別経路のため、予約フローの入口
  // であるこの一覧マウントで記録する（非 roomType の起点＝予約フォーム表示と等価）。
  useEffect(() => {
    recordReservationFormEntryTimeIfNeeded();
  }, []);

  // 拠点ページ（templateLookupKey 照合）と異なり、発行済み StayTicketUnit の実 ID で照合する。
  const stayTicketMasterConfigEntry = STAY_TICKET_MASTER_CONFIG.find(
    (config) => config.base.stayTicketMasterId === stayTicketMasterId,
  );

  const { allAreas, loading, error } = useRoomTypeSearch({
    stayTicketUnitId,
    facilitySlugs: Array.from(facilityFilter),
    evChargerAvailable,
    architectureTypes: Array.from(architectureFilter),
    largeOnly,
    firstNight,
    lastNight,
  });

  const dateRange =
    firstNight !== null && lastNight !== null
      ? { firstNight, lastNight }
      : null;

  // RoomType, Site, Area は相互参照する制約チェーンのため型推論できず、明示的に型引数を渡す
  // （`selectVisibleAreas` の doc コメント参照）。
  const visibleAreas = selectVisibleAreas<
    BrowseRoomType,
    BrowseSite,
    BrowseArea
  >(allAreas, {
    dateRange,
    selectedAreaSlugs: areaFilter,
  });
  const { siteCount, roomTypeCount } = countVisibleInventory(visibleAreas);

  useEffect(() => {
    // dev の StrictMode で effect が二重実行されても二重送信しないよう、送信済みフラグで1回に固定する
    if (!listViewedSentRef.current) {
      listViewedSentRef.current = true;
      gtm.sendEvent("rt_rsv_list_viewed");
    }
  }, [gtm]);

  // rt_rsv_area_filtered 等のフィルタ変更イベント発火時点では RoomTypeBrowseQuery が再フェッチ中で
  // 正しい件数を持てないため、在庫確定（loading完了）のタイミングを別イベントとして切り出す。
  // 初回表示分も送るため useRef 初期値は null にし、直前に送った件数との比較で多重送信を防ぐ。
  const listLoadedSentKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    const key = `${siteCount}_${roomTypeCount}`;
    if (listLoadedSentKeyRef.current === key) return;
    listLoadedSentKeyRef.current = key;
    gtm.sendEvent("rt_rsv_list_loaded", {
      site_count: String(siteCount),
      room_type_count: String(roomTypeCount),
    });
  }, [gtm, loading, siteCount, roomTypeCount]);

  useRoomTypeFilterTracking({
    areaSlugs: areaFilter,
    firstNight,
    lastNight,
    facilitySlugs: facilityFilter,
    evChargerAvailable,
    architectureTypes: architectureFilter,
    largeOnly,
  });

  const setStayRange = (first: PlainDate, last: PlainDate) => {
    void setDateRange({ ci: first, co: last.add({ days: 1 }) });
  };
  const clearStayRange = () => {
    void setDateRange({ ci: null, co: null });
  };

  // 検索カレンダーの日別最低価格・在庫。一覧クエリ（useRoomTypeSearch）とは選択日程に
  // よらず常にカレンダー描画範囲全体を取得する別クエリのため、範囲は今日基準で算出する。
  const today = getCurrentDate();
  const { minDate, renderMaxDate } = getSearchCalendarRenderRange(today);
  // areaFilter は slug（URL 同期のため）だが BE の areaIds 引数は Area の ID を要求するため、
  // allAreas（未フィルタの全エリア一覧）から選択中エリアの ID を引く。
  const selectedAreaIds = allAreas
    .filter((area) => areaFilter.has(area.slug))
    .map((area) => area.id);
  // allAreas ロード中にエリアが選択されていると selectedAreaIds が未確定（空配列）になり、
  // 絞り込みなしの重いクエリが一度発行されたのち allAreas ロード完了後に絞り込みありで
  // 再発行される二重発行が起きるため、ロード完了まで検索カレンダークエリ自体を遅延させる。
  const areaFilterPending = areaFilter.size > 0 && loading;
  const {
    areas: calendarAreas,
    loading: calendarQueryLoading,
    error: dateInfoMapError,
  } = useSearchCalendarAvailability({
    stayTicketUnitId,
    areaIds: selectedAreaIds,
    facilitySlugs: Array.from(facilityFilter),
    evChargerAvailable,
    architectureTypes: Array.from(architectureFilter),
    largeOnly,
    firstNight: minDate,
    lastNight: renderMaxDate,
    skip: areaFilterPending,
  });
  const dateInfoMapLoading = areaFilterPending || calendarQueryLoading;
  const calendarDates = genPlainDateList(minDate, renderMaxDate).map(
    toPlainDateString,
  );
  // allAreas ロード前は selectedAreaIds が空になり絞り込みが効かない瞬間があるため、
  // 日別最低価格・在庫の集計対象にも selectVisibleAreas で同じ絞り込みを冪等に適用する。
  // 検索カレンダーは日程を選ぼうとしている最中でカバレッジ判定が不要なため、dateRange は null にする。
  const visibleCalendarAreas = selectVisibleAreas<
    SearchCalendarRoomType,
    SearchCalendarSite,
    SearchCalendarArea
  >(calendarAreas, {
    dateRange: null,
    selectedAreaSlugs: areaFilter,
  });
  // クエリエラー時は空配列から構築すると全日 disabled になり選択不能に見えるため、
  // dateInfoMap を undefined にして本機能追加前と同じ（価格行・満室表示なしで全日選択可能）
  // 表示にフォールバックする。
  const dateInfoMap = dateInfoMapError
    ? undefined
    : buildSearchCalendarDateInfoMap(visibleCalendarAreas, calendarDates);

  // 絞り込み条件ポップアップ「決定」・セクション「クリア」用の4次元一括適用。
  const applyConditions = ({
    architectureTypes: archTypes,
    facilities: facs,
    largeOnly: large,
    evCharger,
  }: ApplyConditions) => {
    applyArchitectureConditions(archTypes, large);
    applyFacilityConditions(facs, evCharger);
  };

  const reserveContextValue: RoomTypeReserveContextValue = {
    provider: {
      providerType: "StayTicket",
      providerStayTicketUnitId: stayTicketUnitId,
    },
    stayTicketMasterId,
    stayTicketMasterReference:
      stayTicketMasterConfigEntry === undefined
        ? null
        : {
            templateLookupKey:
              stayTicketMasterConfigEntry.base.stayTicketTemplateLookupKey,
          },
    noCancelOptionConfig: stayTicketMasterConfigEntry?.noCancelOption ?? null,
    evChargerAvailable,
    firstNight,
    lastNight,
    minNight,
    maxStayNights,
    unavailablePeriods,
    onDraftSelected: handleDraftSelected,
    setStayRange,
    clearStayRange,
    clearAreaFilter,
  };

  const mainContent = (() => {
    if (loading) return <SiteListSkeleton />;
    if (error) {
      return (
        <p className="font-notojp text-dx-text-sub bg-dx-surface-base p-[16px] text-[14px]">
          部屋タイプを取得できませんでした。時間をおいて再度お試しください。
        </p>
      );
    }
    if (visibleAreas.length === 0) {
      return <RoomTypeSearchEmptyState />;
    }
    return (
      <SiteList
        areas={visibleAreas}
        provider={reserveContextValue.provider}
        facilitySlugs={Array.from(facilityFilter)}
        evChargerAvailable={evChargerAvailable}
        architectureTypes={Array.from(architectureFilter)}
        largeOnly={largeOnly}
      />
    );
  })();

  return (
    <RoomDetailDrawerProvider>
      <RoomTypeReserveProvider value={reserveContextValue}>
        <AvailabilityStripProvider>
          <SiteDetailDrawerProvider onDidClose={handleSiteDetailDrawerDidClose}>
            <SiteDetailDrawerUrlSync siteSlug={siteDetail} />
            <BrowseLayout
              filter={
                <PcSidebar
                  areas={allAreas}
                  selectedAreaSlugs={areaFilter}
                  onToggleArea={toggleArea}
                  onApplyAreaSlugs={applyAreaSlugs}
                  selectedFacilities={facilityFilter}
                  onToggleFacility={toggleFacility}
                  evChargerAvailable={evChargerAvailable}
                  onToggleEvCharger={toggleEvCharger}
                  selectedArchTypes={architectureFilter}
                  onToggleArchType={toggleArchitectureType}
                  largeOnly={largeOnly}
                  onToggleLargeOnly={toggleLargeOnly}
                  onApplyConditions={applyConditions}
                  firstNight={firstNight}
                  lastNight={lastNight}
                  onSetStayRange={setStayRange}
                  onClearStayRange={clearStayRange}
                  dateInfoMap={dateInfoMap}
                  dateInfoMapLoading={dateInfoMapLoading}
                />
              }
              spSearchBar={
                <SpTopbar
                  areas={allAreas}
                  selectedAreaSlugs={areaFilter}
                  onToggleArea={toggleArea}
                  onApplyAreaSlugs={applyAreaSlugs}
                  selectedFacilities={facilityFilter}
                  onToggleFacility={toggleFacility}
                  evChargerAvailable={evChargerAvailable}
                  onToggleEvCharger={toggleEvCharger}
                  selectedArchTypes={architectureFilter}
                  onToggleArchType={toggleArchitectureType}
                  largeOnly={largeOnly}
                  onToggleLargeOnly={toggleLargeOnly}
                  onApplyConditions={applyConditions}
                  firstNight={firstNight}
                  lastNight={lastNight}
                  onSetStayRange={setStayRange}
                  onClearStayRange={clearStayRange}
                  dateInfoMap={dateInfoMap}
                  dateInfoMapLoading={dateInfoMapLoading}
                />
              }
              main={mainContent}
            />
          </SiteDetailDrawerProvider>
        </AvailabilityStripProvider>
      </RoomTypeReserveProvider>
    </RoomDetailDrawerProvider>
  );
}
